import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import HomePage from "@/pages/HomePage";
import GalleryPage from "@/pages/GalleryPage";
import MapPage from "@/pages/MapPage";
import RegisterPetPage from "@/pages/RegisterPetPage";
import MyPetsPage from "@/pages/MyPetsPage";
import OngsPage from "@/pages/OngsPage";
import AuthPage from "@/pages/AuthPage";
import TermosPage from "@/pages/TermosPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Error loading user:", error);
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    loadUser();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, nome) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, nome });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route - redireciona para cadastro ao invés de login
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5D79C]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#6B5344] border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    // Redireciona para login com estado indicando que veio de cadastrar pet
    return <Navigate to="/login?action=cadastrar" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/galeria" element={<GalleryPage />} />
            <Route path="/mapa" element={<MapPage />} />
            <Route path="/ongs" element={<OngsPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/termos" element={<TermosPage />} />
            <Route 
              path="/cadastrar-pet" 
              element={
                <ProtectedRoute>
                  <RegisterPetPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/meus-pets" 
              element={
                <ProtectedRoute>
                  <MyPetsPage />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
